import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { apiFetch, getApiUrl } from '../../config/api';
import './SalesCostingOverview.css';
import { formatAppDate } from '../../utils/dateUtils';
import { useTranslation } from 'react-i18next';

const SalesCostingOverview = forwardRef(({ period = 'month' }, ref) => {
  const { t } = useTranslation('production');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(period);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [useCustomDates, setUseCustomDates] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      setError(null);

      let url = `/api/costing/sales/overview`;
      if (useCustomDates && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `?period=${selectedPeriod}`;
      }

      const response = await apiFetch(getApiUrl(url));
      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        setError(t('salesCostingOverview.fetchFailed'));
      }
    } catch (err) {
      console.error('Error fetching sales costing:', err);
      setError(t('costing.networkError'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!useCustomDates) {
      fetchData();
    }
  }, [selectedPeriod]);

  useEffect(() => {
    if (useCustomDates && startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, useCustomDates]);

  useImperativeHandle(ref, () => ({
    refresh: fetchData,
    isRefreshing: refreshing
  }));

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(num);
  };

  const formatPercentage = (value) => {
    const num = parseFloat(value) || 0;
    return `${num.toFixed(1)}%`;
  };

  const getMarginClass = (percentage) => {
    const pct = parseFloat(percentage) || 0;
    if (pct < 0) return 'negative';
    if (pct < 10) return 'low';
    if (pct < 20) return 'medium';
    return 'good';
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'SHIPPED':
      case 'DELIVERED':
      case 'COMPLETED':
        return 'status-success';
      case 'CONFIRMED':
      case 'IN_PRODUCTION':
        return 'status-info';
      case 'PENDING':
      case 'DRAFT':
        return 'status-warning';
      case 'CANCELLED':
        return 'status-error';
      default:
        return 'status-default';
    }
  };

  const getCostComponentColor = (component) => {
    const colors = {
      'MATERIAL': '#3b82f6',
      'LABOR': '#22c55e',
      'OVERHEAD': '#f59e0b',
      'SHIPPING': '#8b5cf6',
      'PACKAGING': '#ec4899',
      'OTHER': '#6b7280'
    };
    return colors[component] || '#6b7280';
  };

  if (loading) {
    return (
      <div className="sales-costing-overview loading">
        <div className="loading-spinner"></div>
        <p>{t('salesCostingOverview.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sales-costing-overview error">
        <p>{error}</p>
        <button onClick={fetchData} className="btn-retry">
          {t('common:ui.tryAgain')}
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="sales-costing-overview empty">
        <p>{t('salesCostingOverview.noData')}</p>
      </div>
    );
  }

  const { summary, orders } = data;
  const lowMarginAlerts = data.low_margin_alerts || [];

  return (
    <div className="sales-costing-overview">
      {/* Period Selector */}
      <div className="period-selector">
        <div className="period-toggle">
          <button
            className={`toggle-btn ${!useCustomDates ? 'active' : ''}`}
            onClick={() => setUseCustomDates(false)}
          >
            {t('costing.period.preset')}
          </button>
          <button
            className={`toggle-btn ${useCustomDates ? 'active' : ''}`}
            onClick={() => setUseCustomDates(true)}
          >
            {t('costing.period.custom')}
          </button>
        </div>

        {!useCustomDates ? (
          <>
            <label>{t('costing.period.label')}:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              disabled={refreshing}
            >
              <option value="today">{t('costing.period.today')}</option>
              <option value="week">{t('costing.period.week')}</option>
              <option value="month">{t('costing.period.month')}</option>
              <option value="quarter">{t('costing.period.quarter')}</option>
              <option value="year">{t('costing.period.year')}</option>
              <option value="last_month">{t('costing.period.lastMonth')}</option>
              <option value="all">{t('costing.period.all')}</option>
            </select>
          </>
        ) : (
          <>
            <label>{t('costing.period.from')}:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={refreshing}
              className="date-input"
            />
            <label>{t('costing.period.to')}:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={refreshing}
              className="date-input"
            />
          </>
        )}

        <button
          onClick={fetchData}
          disabled={refreshing || (useCustomDates && (!startDate || !endDate))}
          className="btn-refresh"
        >
          {refreshing ? t('costing.refreshing') : t('common:ui.refresh')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card revenue">
          <div className="card-icon">💰</div>
          <div className="card-content">
            <span className="card-label">{t('costing.summary.totalRevenue')}</span>
            <span className="card-value">{formatCurrency(summary.total_revenue)}</span>
            <span className="card-sub">{t('costing.summary.ordersCount', { count: summary.order_count })}</span>
          </div>
        </div>

        <div className="summary-card cost">
          <div className="card-icon">📊</div>
          <div className="card-content">
            <span className="card-label">{t('costing.summary.totalCost')}</span>
            <span className="card-value">{formatCurrency(summary.total_cost)}</span>
            <span className="card-sub cost-breakdown-sub">
              {t('salesCostingOverview.bomWasteBreakdown', { bom: formatCurrency(summary.bom_cost || 0), waste: formatCurrency(summary.waste_cost || 0) })}
            </span>
          </div>
        </div>

        <div className="summary-card margin">
          <div className="card-icon">📈</div>
          <div className="card-content">
            <span className="card-label">{t('salesCostingOverview.grossMargin')}</span>
            <span className="card-value">{formatCurrency(summary.gross_margin)}</span>
            <span className={`card-sub margin-pct ${getMarginClass(summary.average_margin_percentage)}`}>
              {t('costing.summary.average', { value: formatPercentage(summary.average_margin_percentage) })}
            </span>
          </div>
        </div>

        <div className="summary-card alerts">
          <div className="card-icon">⚠️</div>
          <div className="card-content">
            <span className="card-label">{t('salesCostingOverview.lowMarginOrders')}</span>
            <span className="card-value">{lowMarginAlerts.length}</span>
            <span className="card-sub">{t('salesCostingOverview.below10')}</span>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div className="cost-breakdown-section">
        <h3>{t('salesCostingOverview.costBreakdown')}</h3>
        {(parseFloat(summary.bom_cost) > 0 || parseFloat(summary.waste_cost) > 0) ? (
          <div className="cost-breakdown">
            <div className="breakdown-bars">
              {/* BOM Cost Bar */}
              {parseFloat(summary.bom_cost) > 0 && (
                <div className="breakdown-item">
                  <div className="breakdown-label">
                    <span className="component-name">{t('salesCostingOverview.productCost')}</span>
                    <span className="component-values">
                      {formatCurrency(summary.bom_cost)}
                      ({formatPercentage(parseFloat(summary.total_cost) > 0
                        ? (parseFloat(summary.bom_cost) / parseFloat(summary.total_cost) * 100)
                        : 0)})
                    </span>
                  </div>
                  <div className="breakdown-bar-container">
                    <div
                      className="breakdown-bar"
                      style={{
                        width: `${parseFloat(summary.total_cost) > 0
                          ? Math.min((parseFloat(summary.bom_cost) / parseFloat(summary.total_cost) * 100), 100)
                          : 0}%`,
                        backgroundColor: '#3b82f6'
                      }}
                    />
                  </div>
                </div>
              )}
              {/* Waste Cost Bar */}
              {parseFloat(summary.waste_cost) > 0 && (
                <div className="breakdown-item">
                  <div className="breakdown-label">
                    <span className="component-name">{t('salesCostingOverview.wasteCost')}</span>
                    <span className="component-values">
                      {formatCurrency(summary.waste_cost)}
                      ({formatPercentage(parseFloat(summary.total_cost) > 0
                        ? (parseFloat(summary.waste_cost) / parseFloat(summary.total_cost) * 100)
                        : 0)})
                    </span>
                  </div>
                  <div className="breakdown-bar-container">
                    <div
                      className="breakdown-bar waste-bar"
                      style={{
                        width: `${parseFloat(summary.total_cost) > 0
                          ? Math.min((parseFloat(summary.waste_cost) / parseFloat(summary.total_cost) * 100), 100)
                          : 0}%`,
                        backgroundColor: '#ef4444'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="empty-state">{t('salesCostingOverview.noCostData')}</div>
        )}
      </div>

      {/* Low margin alerts: every order of the period below 10 %, worst first (backend low_margin_alerts) */}
      {lowMarginAlerts.length > 0 && (
        <div className="orders-section low-margin-alerts">
          <h3>{t('salesCostingOverview.lowMarginAlerts')}</h3>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>{t('costing.columns.orderNumber')}</th>
                  <th>{t('common:ui.customer')}</th>
                  <th className="text-right">{t('costing.columns.margin')}</th>
                  <th className="text-right">{t('costing.columns.marginPct')}</th>
                  <th className="text-right">{t('costing.columns.waste')}</th>
                </tr>
              </thead>
              <tbody>
                {lowMarginAlerts.map((alert) => (
                  <tr key={alert.order_id} className={`margin-row-${getMarginClass(alert.margin_percentage)}`}>
                    <td><strong>{alert.order_number}</strong></td>
                    <td>{alert.customer_name}</td>
                    <td className="text-right">{formatCurrency(alert.margin_amount)}</td>
                    <td className={`text-right ${getMarginClass(alert.margin_percentage)}`}>
                      {formatPercentage(alert.margin_percentage)}
                    </td>
                    <td className="text-right waste-cell">{formatCurrency(alert.waste_cost || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className="orders-section">
        <h3>{t('salesCostingOverview.orderProfitability')}</h3>
        {orders && orders.length > 0 ? (
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th>{t('costing.columns.orderNumber')}</th>
                  <th>{t('common:ui.customer')}</th>
                  <th>{t('common:ui.date')}</th>
                  <th className="text-right">{t('costing.columns.revenue')}</th>
                  <th className="text-right">{t('costing.columns.bomCost')}</th>
                  <th className="text-right">{t('costing.columns.waste')}</th>
                  <th className="text-right">{t('costing.columns.totalCost')}</th>
                  <th className="text-right">{t('costing.columns.margin')}</th>
                  <th className="text-right">{t('costing.columns.marginPct')}</th>
                  <th>{t('common:ui.status')}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => {
                  const marginClass = getMarginClass(order.margin_percentage);
                  const isLowMargin = marginClass === 'negative' || marginClass === 'low';
                  return (
                    <tr key={index} className={`margin-row-${marginClass}`}>
                      <td>
                        <strong>{order.order_number}</strong>
                        {isLowMargin && <span className="low-margin-badge">{t('salesCostingOverview.lowMarginBadge')}</span>}
                      </td>
                      <td>{order.customer_name}</td>
                      <td>{formatAppDate(order.order_date)}</td>
                      <td className="text-right">{formatCurrency(order.total_revenue)}</td>
                      <td className="text-right">{formatCurrency(order.bom_cost || 0)}</td>
                      <td className="text-right waste-cell">{formatCurrency(order.waste_cost || 0)}</td>
                      <td className="text-right">{formatCurrency(order.total_cost)}</td>
                      <td className="text-right">{formatCurrency(order.margin_amount)}</td>
                      <td className={`text-right ${marginClass}`}>
                        {formatPercentage(order.margin_percentage)}
                      </td>
                      <td>
                        <span className={`status-badge ${getStatusClass(order.status)}`}>
                          {t(`costing.orderStatus.${order.status?.toUpperCase()}`, { defaultValue: order.status })}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">{t('costing.noOrdersForPeriod')}</div>
        )}
      </div>
    </div>
  );
});

export default SalesCostingOverview;
